import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
} from 'antd';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import BreadCrumbs from '../../../../common/components/Breadcrumbs';
import { validateMessages } from '../../../../common/constants/common';
import { Page } from '../../../../common/constants/pageAction';
import {
  forEditModal,
  getObjectForUpdateMultiple,
  getSimpleDate,
  passDateToApi,
} from '../../../../common/helperFunction';
import { IInlineSearch } from '../../../../common/models/common';
import { ILookup } from '../../../../services/common/common.model';
import { ISlim360O365UserCopilotActivitySummary } from '../../../../services/slim360/o365UserCopilotActivitySummary/o365UserCopilotActivitySummary.model';
import { useAppSelector, useAppDispatch } from '../../../../store/app.hooks';
import {
  getBULookup,
  getCompanyLookup,
  getTenantLookup,
  updateMultiple,
} from '../../../../store/common/common.action';
import {
  clearBULookUp,
  clearCompanyLookUp,
  clearMultipleUpdateMessages,
  commonSelector,
} from '../../../../store/common/common.reducer';
import { globalSearchSelector } from '../../../../store/globalSearch/globalSearch.reducer';
import {
  saveSlim360O365UserCopilotActivitySummary,
  getSlim360O365UserCopilotActivitySummaryById,
} from '../../../../store/slim360/o365UserCopilotActivitySummary/o365UserCopilotActivitySummary.action';
import {
  clearSlim360O365UserCopilotActivitySummaryMessages,
  clearSlim360O365UserCopilotActivitySummaryGetById,
  slim360O365UserCopilotActivitySummarySelector,
} from '../../../../store/slim360/o365UserCopilotActivitySummary/o365UserCopilotActivitySummary.reducer';
import { IAddSlim360O365UserCopilotActivitySummaryProps } from './addO365UserCopilotActivitySummary.model';

const { Option } = Select;

const AddSlim360O365UserCopilotActivitySummaryModal: React.FC<IAddSlim360O365UserCopilotActivitySummaryProps> =
  (props) => {
    const slim360O365UserCopilotActivitySummary = useAppSelector(
      slim360O365UserCopilotActivitySummarySelector
    );
    const commonLookups = useAppSelector(commonSelector);
    const dispatch = useAppDispatch();
    const globalFilters = useAppSelector(globalSearchSelector);

    const { id, showModal, handleModalClose, refreshDataTable, isMultiple, valuesForSelection } =
      props;

    const isNew: boolean = id || isMultiple ? false : true;
    const title = useMemo(() => {
      return (
        <>
          {isNew ? 'Add ' : 'Edit '}{' '}
          <BreadCrumbs pageName={Page.Slim360O365UserCopilotActivitySummary} level={1} />
        </>
      );
    }, [isNew]);
    const submitButtonText = useMemo(() => {
      return isNew ? 'Save' : 'Update';
    }, [isNew]);

    const [form] = Form.useForm();

    let initialValues: ISlim360O365UserCopilotActivitySummary = {
      bu_id: null,
      company_id: null,
      tenant_id: null,
      azure_tenant_id: '',
      user_principal_name: '',
      report_period: null,
      year: null,
      week: null,
      score: null,
      weight: null,
      score_teams: null,
      weight_teams: null,
      bizchat: null,
      excel: null,
      loop: null,
      other: null,
      outlook: null,
      powerpoint: null,
      teams_chat: null,
      teams_meeting: null,
      whiteboard: null,
      word: null,
      last_calculated_date: null,
      last_modified_date: null,
      slim_id: null,
      date_added: getSimpleDate(),
    };

    const onFinish = (values: any) => {
      const inputValues: ISlim360O365UserCopilotActivitySummary = {
        ...values,
        id: id ? +id : null,
      };
      inputValues.date_added = passDateToApi(inputValues.date_added, true);
      inputValues.report_period = inputValues.report_period
        ? passDateToApi(inputValues.report_period, true)
        : null;
      inputValues.last_calculated_date = inputValues.last_calculated_date
        ? passDateToApi(inputValues.last_calculated_date, true)
        : null;
      inputValues.last_modified_date = inputValues.last_modified_date
        ? passDateToApi(inputValues.last_modified_date, true)
        : null;
      if (!isMultiple) {
        dispatch(saveSlim360O365UserCopilotActivitySummary(inputValues));
      } else {
        const result = getObjectForUpdateMultiple(
          valuesForSelection,
          inputValues,
          slim360O365UserCopilotActivitySummary.search.tableName
        );
        if (result) {
          dispatch(updateMultiple(result));
        }
      }
    };

    const handleTenantChange = (tenantId: number) => {
      form.setFieldsValue({ tenant_id: tenantId, company_id: null, bu_id: null });
      if (tenantId) {
        dispatch(getCompanyLookup(tenantId));
        dispatch(clearBULookUp());
      } else {
        dispatch(clearCompanyLookUp());
        dispatch(clearBULookUp());
      }
    };

    const handleCompanyChange = (companyId: number) => {
      form.setFieldsValue({ company_id: companyId, bu_id: null });
      if (companyId) {
        dispatch(getBULookup(companyId));
      } else {
        dispatch(clearBULookUp());
      }
    };

    const handleBUChange = (buId: number) => {
      form.setFieldsValue({ bu_id: buId });
    };

    const fillValuesOnEdit = async (data: ISlim360O365UserCopilotActivitySummary) => {
      if (data.tenant_id) {
        await dispatch(getCompanyLookup(data.tenant_id));
      }
      if (data.company_id) {
        await dispatch(getBULookup(data.company_id));
      }
      if (data) {
        initialValues = {
          tenant_id: _.isNull(data.tenant_id) ? null : data.tenant_id,
          company_id: _.isNull(data.company_id) ? null : data.company_id,
          bu_id: _.isNull(data.bu_id) ? null : data.bu_id,
          azure_tenant_id: data.azure_tenant_id,
          user_principal_name: data.user_principal_name,
          report_period: _.isNull(data.report_period) ? null : forEditModal(data.report_period),
          year: data.year,
          week: data.week,
          score: data.score,
          weight: data.weight,
          score_teams: data.score_teams,
          weight_teams: data.weight_teams,
          bizchat: data.bizchat,
          excel: data.excel,
          loop: data.loop,
          other: data.other,
          outlook: data.outlook,
          powerpoint: data.powerpoint,
          teams_chat: data.teams_chat,
          teams_meeting: data.teams_meeting,
          whiteboard: data.whiteboard,
          word: data.word,
          last_calculated_date: _.isNull(data.last_calculated_date)
            ? null
            : forEditModal(data.last_calculated_date),
          last_modified_date: _.isNull(data.last_modified_date)
            ? null
            : forEditModal(data.last_modified_date),
          slim_id: data.slim_id,
          date_added: _.isNull(data.date_added) ? null : forEditModal(data.date_added),
        };
        form.setFieldsValue(initialValues);
      }
    };

    useEffect(() => {
      if (slim360O365UserCopilotActivitySummary.save.messages.length > 0) {
        if (slim360O365UserCopilotActivitySummary.save.hasErrors) {
          toast.error(slim360O365UserCopilotActivitySummary.save.messages.join(' '));
        } else {
          toast.success(slim360O365UserCopilotActivitySummary.save.messages.join(' '));
          handleModalClose();
          refreshDataTable();
        }
        dispatch(clearSlim360O365UserCopilotActivitySummaryMessages());
      }
    }, [slim360O365UserCopilotActivitySummary.save.messages]);

    useEffect(() => {
      if (commonLookups.save.messages.length > 0) {
        if (commonLookups.save.hasErrors) {
          toast.error(commonLookups.save.messages.join(' '));
        } else {
          toast.warn(commonLookups.save.messages.join(' '));
          handleModalClose();
        }
        dispatch(clearMultipleUpdateMessages());
      }
    }, [commonLookups.save.messages]);

    useEffect(() => {
      if (+id > 0 && slim360O365UserCopilotActivitySummary.getById.data) {
        const data = slim360O365UserCopilotActivitySummary.getById.data;
        fillValuesOnEdit(data);
      }
    }, [slim360O365UserCopilotActivitySummary.getById.data]);

    useEffect(() => {
      if (Object.keys(globalFilters?.globalTenantLookup?.data).length == 0) {
        dispatch(getTenantLookup());
      }
      if (+id > 0) {
        dispatch(getSlim360O365UserCopilotActivitySummaryById(+id));
      }
      return () => {
        dispatch(clearSlim360O365UserCopilotActivitySummaryGetById());
        dispatch(clearCompanyLookUp());
        dispatch(clearBULookUp());
      };
    }, [dispatch]);

    useEffect(() => {
      if (+id === 0 && !isMultiple) {
        const globalSearch: IInlineSearch = {};
        for (const key in globalFilters.search) {
          const element = globalFilters.search[key];
          globalSearch[key] = element ? [element] : null;
        }
        if (globalFilters.search.tenant_id && globalFilters.search.tenant_id !== 0) {
          if (!globalFilters.search.company_id) {
            dispatch(getCompanyLookup(globalSearch.tenant_id[0]));
          }
          if (!globalFilters.search.bu_id && globalFilters.search.company_id !== 0) {
            dispatch(getBULookup(globalSearch.company_id[0]));
          }
          const initlValues = {
            company_id: _.isNull(globalSearch.company_id) ? null : globalSearch.company_id[0],
            bu_id: _.isNull(globalSearch.bu_id) ? null : globalSearch.bu_id[0],
            tenant_id: _.isNull(globalSearch.tenant_id) ? null : globalSearch.tenant_id[0],
          };
          form.setFieldsValue(initlValues);
        }
      }
    }, []);

    return (
      <>
        <Modal
          wrapClassName="custom-modal"
          title={title}
          centered
          visible={showModal}
          onCancel={handleModalClose}
          footer={false}
        >
          {slim360O365UserCopilotActivitySummary.getById.loading ? (
            <div className="spin-loader">
              <Spin spinning={slim360O365UserCopilotActivitySummary.getById.loading} />
            </div>
          ) : (
            <Form
              form={form}
              name="slim360O365UserCopilotActivitySummary"
              initialValues={initialValues}
              onFinish={onFinish}
              validateMessages={validateMessages}
            >
              <Row gutter={[30, 15]} className="form-label-hide">
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'tenant_id']} valuePropName="checked" noStyle>
                        <Checkbox>Tenant</Checkbox>
                      </Form.Item>
                    ) : (
                      'Tenant'
                    )}
                    <Form.Item
                      name="tenant_id"
                      className="m-0"
                      label="Tenant"
                      rules={[{ required: !isMultiple }]}
                    >
                      <Select
                        onChange={handleTenantChange}
                        allowClear
                        loading={commonLookups.tenantLookup.loading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option: any) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        filterSort={(optionA: any, optionB: any) =>
                          optionA.children
                            ?.toLowerCase()
                            ?.localeCompare(optionB.children?.toLowerCase())
                        }
                      >
                        {Object.keys(globalFilters?.globalTenantLookup?.data).length > 0
                          ? globalFilters?.globalTenantLookup?.data.map((option: ILookup) => (
                              <Option key={option.id} value={option.id}>
                                {option.name}
                              </Option>
                            ))
                          : commonLookups.tenantLookup.data.map((option: ILookup) => (
                              <Option key={option.id} value={option.id}>
                                {option.name}
                              </Option>
                            ))}
                      </Select>
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'company_id']} valuePropName="checked" noStyle>
                        <Checkbox>Company</Checkbox>
                      </Form.Item>
                    ) : (
                      'Company'
                    )}
                    <Form.Item
                      name="company_id"
                      className="m-0"
                      label="Company"
                      rules={[{ required: !isMultiple }]}
                    >
                      <Select
                        onChange={handleCompanyChange}
                        allowClear
                        loading={commonLookups.companyLookup.loading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option: any) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        filterSort={(optionA: any, optionB: any) =>
                          optionA.children
                            ?.toLowerCase()
                            ?.localeCompare(optionB.children?.toLowerCase())
                        }
                      >
                        {Object.keys(commonLookups.companyLookup.data).length > 0
                          ? commonLookups.companyLookup.data.map((option: ILookup) => (
                              <Option key={option.id} value={option.id}>
                                {option.name}
                              </Option>
                            ))
                          : globalFilters?.globalCompanyLookup?.data.map((option: ILookup) => (
                              <Option key={option.id} value={option.id}>
                                {option.name}
                              </Option>
                            ))}
                      </Select>
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'bu_id']} valuePropName="checked" noStyle>
                        <Checkbox>BU</Checkbox>
                      </Form.Item>
                    ) : (
                      'BU'
                    )}
                    <Form.Item name="bu_id" className="m-0" label="BU">
                      <Select
                        onChange={handleBUChange}
                        allowClear
                        loading={commonLookups.buLookup.loading}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option: any) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        filterSort={(optionA: any, optionB: any) =>
                          optionA.children
                            ?.toLowerCase()
                            ?.localeCompare(optionB.children?.toLowerCase())
                        }
                      >
                        {Object.keys(commonLookups.buLookup.data).length > 0
                          ? commonLookups.buLookup.data.map((option: ILookup) => (
                              <Option key={option.id} value={option.id}>
                                {option.name}
                              </Option>
                            ))
                          : globalFilters?.globalBULookup?.data.map((option: ILookup) => (
                              <Option key={option.id} value={option.id}>
                                {option.name}
                              </Option>
                            ))}
                      </Select>
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item
                        name={['checked', 'azure_tenant_id']}
                        valuePropName="checked"
                        noStyle
                      >
                        <Checkbox>Azure Tenant Id</Checkbox>
                      </Form.Item>
                    ) : (
                      'Azure Tenant Id'
                    )}
                    <Form.Item
                      name="azure_tenant_id"
                      className="m-0"
                      label="Azure Tenant Id"
                      rules={[{ required: !isMultiple, max: 36 }]}
                    >
                      <Input className="form-control" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item
                        name={['checked', 'user_principal_name']}
                        valuePropName="checked"
                        noStyle
                      >
                        <Checkbox>User Principal Name</Checkbox>
                      </Form.Item>
                    ) : (
                      'User Principal Name'
                    )}
                    <Form.Item
                      name="user_principal_name"
                      className="m-0"
                      label="User Principal Name"
                      rules={[{ required: !isMultiple, max: 255 }]}
                    >
                      <Input className="form-control" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'date_added']} valuePropName="checked" noStyle>
                        <Checkbox>Date Added</Checkbox>
                      </Form.Item>
                    ) : (
                      'Date Added'
                    )}
                    <Form.Item
                      name="date_added"
                      label="Date Added"
                      className="m-0"
                      rules={[{ required: !isMultiple }]}
                    >
                      <DatePicker className="form-control w-100" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item
                        name={['checked', 'report_period']}
                        valuePropName="checked"
                        noStyle
                      >
                        <Checkbox>Report Period</Checkbox>
                      </Form.Item>
                    ) : (
                      'Report Period'
                    )}
                    <Form.Item name="report_period" className="m-0" label="Report Period">
                      <DatePicker className="form-control w-100" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'year']} valuePropName="checked" noStyle>
                        <Checkbox>Year</Checkbox>
                      </Form.Item>
                    ) : (
                      'Year'
                    )}
                    <Form.Item
                      name="year"
                      className="m-0"
                      label="Year"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'week']} valuePropName="checked" noStyle>
                        <Checkbox>Week</Checkbox>
                      </Form.Item>
                    ) : (
                      'Week'
                    )}
                    <Form.Item
                      name="week"
                      className="m-0"
                      label="Week"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'score']} valuePropName="checked" noStyle>
                        <Checkbox>Score</Checkbox>
                      </Form.Item>
                    ) : (
                      'Score'
                    )}
                    <Form.Item
                      name="score"
                      className="m-0"
                      label="Score"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'weight']} valuePropName="checked" noStyle>
                        <Checkbox>Weight</Checkbox>
                      </Form.Item>
                    ) : (
                      'Weight'
                    )}
                    <Form.Item
                      name="weight"
                      className="m-0"
                      label="Weight"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'score_teams']} valuePropName="checked" noStyle>
                        <Checkbox>Score Teams</Checkbox>
                      </Form.Item>
                    ) : (
                      'Score Teams'
                    )}
                    <Form.Item
                      name="score_teams"
                      className="m-0"
                      label="Score Teams"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'weight_teams']} valuePropName="checked" noStyle>
                        <Checkbox>Weight Teams</Checkbox>
                      </Form.Item>
                    ) : (
                      'Weight Teams'
                    )}
                    <Form.Item
                      name="weight_teams"
                      className="m-0"
                      label="Weight Teams"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'bizchat']} valuePropName="checked" noStyle>
                        <Checkbox>BizChat</Checkbox>
                      </Form.Item>
                    ) : (
                      'BizChat'
                    )}
                    <Form.Item
                      name="bizchat"
                      className="m-0"
                      label="BizChat"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'excel']} valuePropName="checked" noStyle>
                        <Checkbox>Excel</Checkbox>
                      </Form.Item>
                    ) : (
                      'Excel'
                    )}
                    <Form.Item
                      name="excel"
                      className="m-0"
                      label="Excel"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'loop']} valuePropName="checked" noStyle>
                        <Checkbox>Loop</Checkbox>
                      </Form.Item>
                    ) : (
                      'Loop'
                    )}
                    <Form.Item
                      name="loop"
                      className="m-0"
                      label="Loop"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'other']} valuePropName="checked" noStyle>
                        <Checkbox>Other</Checkbox>
                      </Form.Item>
                    ) : (
                      'Other'
                    )}
                    <Form.Item
                      name="other"
                      className="m-0"
                      label="Other"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'outlook']} valuePropName="checked" noStyle>
                        <Checkbox>Outlook</Checkbox>
                      </Form.Item>
                    ) : (
                      'Outlook'
                    )}
                    <Form.Item
                      name="outlook"
                      className="m-0"
                      label="Outlook"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'powerpoint']} valuePropName="checked" noStyle>
                        <Checkbox>PowerPoint</Checkbox>
                      </Form.Item>
                    ) : (
                      'PowerPoint'
                    )}
                    <Form.Item
                      name="powerpoint"
                      className="m-0"
                      label="PowerPoint"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'teams_chat']} valuePropName="checked" noStyle>
                        <Checkbox>Teams Chat</Checkbox>
                      </Form.Item>
                    ) : (
                      'Teams Chat'
                    )}
                    <Form.Item
                      name="teams_chat"
                      className="m-0"
                      label="Teams Chat"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item
                        name={['checked', 'teams_meeting']}
                        valuePropName="checked"
                        noStyle
                      >
                        <Checkbox>Teams Meeting</Checkbox>
                      </Form.Item>
                    ) : (
                      'Teams Meeting'
                    )}
                    <Form.Item
                      name="teams_meeting"
                      className="m-0"
                      label="Teams Meeting"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'whiteboard']} valuePropName="checked" noStyle>
                        <Checkbox>Whiteboard</Checkbox>
                      </Form.Item>
                    ) : (
                      'Whiteboard'
                    )}
                    <Form.Item
                      name="whiteboard"
                      className="m-0"
                      label="Whiteboard"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'word']} valuePropName="checked" noStyle>
                        <Checkbox>Word</Checkbox>
                      </Form.Item>
                    ) : (
                      'Word'
                    )}
                    <Form.Item
                      name="word"
                      className="m-0"
                      label="Word"
                      rules={[{ type: 'number' }]}
                    >
                      <InputNumber className="form-control w-100" precision={0} />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item
                        name={['checked', 'last_calculated_date']}
                        valuePropName="checked"
                        noStyle
                      >
                        <Checkbox>Last Calculated Date</Checkbox>
                      </Form.Item>
                    ) : (
                      'Last Calculated Date'
                    )}
                    <Form.Item
                      name="last_calculated_date"
                      className="m-0"
                      label="Last Calculated Date"
                    >
                      <DatePicker className="form-control w-100" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item
                        name={['checked', 'last_modified_date']}
                        valuePropName="checked"
                        noStyle
                      >
                        <Checkbox>Last Modified Date</Checkbox>
                      </Form.Item>
                    ) : (
                      'Last Modified Date'
                    )}
                    <Form.Item name="last_modified_date" className="m-0" label="Last Modified Date">
                      <DatePicker className="form-control w-100" />
                    </Form.Item>
                  </div>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <div className="form-group m-0">
                    {isMultiple ? (
                      <Form.Item name={['checked', 'slim_id']} valuePropName="checked" noStyle>
                        <Checkbox>SLIM360 Id</Checkbox>
                      </Form.Item>
                    ) : (
                      'SLIM360 Id'
                    )}
                    <Form.Item
                      name="slim_id"
                      className="m-0"
                      label="SLIM360 Id"
                      rules={[{ max: 50 }]}
                    >
                      <Input className="form-control" />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <div className="btns-block modal-footer">
                <Button
                  key="submit"
                  type="primary"
                  htmlType="submit"
                  loading={
                    slim360O365UserCopilotActivitySummary.save.loading || commonLookups.save.loading
                  }
                >
                  {submitButtonText}
                </Button>
                <Button key="back" onClick={handleModalClose}>
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Modal>
      </>
    );
  };
export default AddSlim360O365UserCopilotActivitySummaryModal;
